import { useApiWithAuth } from "@/modules/api";
import { PermissionPayload } from "@/modules/auth";
import { ClientePayload } from "@/modules/business/cliente/clienteBusiness";
import { clearObject } from "@/modules/utils";
import { ServicoVeiculoRntrc, formatPlaca } from "../rntrcTypes";
import {
  OrigemPreenchimentoAntt,
  PessoaAnttPayload
} from "../pessoa/pessoaAnttBusiness";

/**
 * Veículo do cadastro próprio da ANTT: placa, renavam, chassi, tipo (tração ou tracionado),
 * proprietário (quem consta no CRLV) e o CRLV. Separado do cadastro de veículo da GovPass.
 */
export interface VeiculoAnttPayload {
  idVeiculoAntt?: number | null;
  cliente?: ClientePayload | null;
  placa?: string | null;
  renavam?: string | null;
  chassi?: string | null;
  flServicoVeiculo?: ServicoVeiculoRntrc | null;
  proprietario?: PessoaAnttPayload | null;
  possuiCrlv?: boolean | null;
  /** Upload novo do CRLV (key do storage temporário). */
  keyBlCrlvTemp?: string | null;
  /** Veículo da GovPass que preencheu a tela: o salvar copia o CRLV dele. */
  idVeiculoGovpass?: number | null;
  nomeProprietarioCrlvGovpass?: string | null;
  possuiCrlvGovpass?: boolean | null;
  origem?: OrigemPreenchimentoAntt | null;
  flExclusao?: boolean | null;
  dataCadastro?: Date | null;
  dataAlteracao?: Date | null;
  permissionInfo?: PermissionPayload;
}

export interface VeiculoAnttFilterPayload {
  idCliente?: number | null;
  placa?: string | null;
  idPessoaAnttProprietario?: number | null;
  cpfCnpjProprietario?: string | null;
  flServicoVeiculo?: ServicoVeiculoRntrc | null;
  arquivado?: boolean | null;
}

export const useVeiculoAnttService = () => {
  const {
    data,
    loading,
    get,
    post,
    put,
    patch,
    downloadFile,
    downloadFileNewTab
  } = useApiWithAuth();

  const searchVeiculoAntt = (
    filter: VeiculoAnttFilterPayload,
    page: number
  ): Promise<void> => {
    return get("/rntrc/veiculo-antt", { ...clearObject({ ...filter }), page }, {
      skipEmptyToast: true
    } as Record<string, unknown>);
  };

  const getVeiculoAntt = (idVeiculoAntt: number): Promise<void> => {
    return get(`/rntrc/veiculo-antt/${idVeiculoAntt}`);
  };

  const newVeiculoAntt = (idCliente?: number | null): Promise<void> => {
    return get("/rntrc/veiculo-antt/new", idCliente ? { idCliente } : {});
  };

  /** O veículo ANTT da placa, ou o que a GovPass tem dela (só do próprio cliente). */
  const preencherVeiculoAntt = (
    placa: string,
    idCliente?: number | null
  ): Promise<void> => {
    return get(
      "/rntrc/veiculo-antt/preencher",
      clearObject({ placa, idCliente }),
      {
        skipEmptyToast: true
      } as Record<string, unknown>
    );
  };

  /**
   * Lê o CRLV em PDF (o digital) e devolve placa, renavam e tipo para preencher a tela; nada é
   * gravado. Foto ou imagem não tem leitura: o usuário preenche à mão.
   */
  const lerCrlv = (formData: FormData): Promise<void> => {
    return post("/rntrc/veiculo-antt/ler-crlv", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  };

  /**
   * Busca de placa da tela de serviço: veículos ANTT do cliente e, com parte da placa, os da
   * GovPass que ainda não estão na ANTT (origem "GOV", sem idVeiculoAntt).
   */
  const buscarVeiculosParaPreenchimento = (
    placa: string | null,
    idCliente?: number | null
  ): Promise<void> => {
    return get(
      "/rntrc/veiculo-antt/preencher/busca",
      clearObject({ placa, idCliente }),
      {
        skipEmptyToast: true
      } as Record<string, unknown>
    );
  };

  const saveNewVeiculoAntt = (entity: VeiculoAnttPayload): Promise<void> => {
    return post("/rntrc/veiculo-antt", entity);
  };

  const updateVeiculoAntt = (
    idVeiculoAntt: number,
    entity: VeiculoAnttPayload
  ): Promise<void> => {
    return put(`/rntrc/veiculo-antt/${idVeiculoAntt}`, entity);
  };

  const shelveVeiculoAntt = (idVeiculoAntt: number): Promise<void> => {
    return patch(`/rntrc/veiculo-antt/${idVeiculoAntt}/arquivar`);
  };

  const unshelveVeiculoAntt = (idVeiculoAntt: number): Promise<void> => {
    return patch(`/rntrc/veiculo-antt/${idVeiculoAntt}/desarquivar`);
  };

  /** Abre o CRLV pelo link temporário. */
  const abrirCrlv = (
    idVeiculoAntt: number,
    newTab?: boolean
  ): Promise<void> => {
    return (newTab ? downloadFileNewTab : downloadFile)(
      `/rntrc/veiculo-antt/${idVeiculoAntt}/crlv`
    );
  };

  return {
    data,
    loading,
    searchVeiculoAntt,
    getVeiculoAntt,
    newVeiculoAntt,
    preencherVeiculoAntt,
    lerCrlv,
    buscarVeiculosParaPreenchimento,
    saveNewVeiculoAntt,
    updateVeiculoAntt,
    shelveVeiculoAntt,
    unshelveVeiculoAntt,
    abrirCrlv
  };
};

export const safeVeiculoAntt = (
  entity?: VeiculoAnttPayload | null
): VeiculoAnttPayload => {
  const ret = entity ?? { placa: "", renavam: "", chassi: "" };
  ret.proprietario = ret.proprietario ?? { cpfCnpj: "", nome: "" };
  return ret;
};

export const safeFilterVeiculoAntt = (): VeiculoAnttFilterPayload => {
  return { placa: "", arquivado: false };
};

export const getNomeVeiculoAntt = (entity?: VeiculoAnttPayload | null) => {
  if (!entity || !entity.placa) {
    return "";
  }
  return (
    formatPlaca(entity.placa) +
    (entity.proprietario?.nome ? " - " + entity.proprietario.nome : "")
  );
};
